// src/pages/TermsOfServicePage.js
import React from 'react';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';

const TermsOfServicePage = () => {
  return (
    <div className="flex flex-col min-h-screen bg-background-primary">
      <MainNavbar />

      <main className="flex-grow">
        <div className="max-w-4xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          {/* Main Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-extrabold text-content-primary sm:text-5xl tracking-tight">Terms of Service</h1>
            <p className="mt-4 text-lg text-content-secondary">Last updated: March 6, 2026</p>
          </div>

          {/* Article content with prose styling */}
          <article className="prose prose-lg lg:prose-xl max-w-none dark:prose-invert">

            <h2>1. Introduction</h2>
            <p>
              Welcome to Kikikata. By accessing or using our website and services, you agree to be bound by these Terms of Service. Please read them
              carefully. If you do not agree with any part of these terms, you must not use our services.
            </p>

            <h2>2. Eligibility</h2>
            <p>
              You must be at least 13 years old to use Kikikata. By using our services, you represent that you are at least 13 years of age and have
              the legal capacity to enter into these terms. If you are between 13 and 18 years old, you represent that your parent or legal guardian
              has reviewed and agreed to these terms on your behalf.
            </p>

            <h2>3. User Accounts</h2>
            <p>
              To access certain features of Kikikata, you must create an account using your email address or a Google account. You are responsible
              for maintaining the confidentiality of your account credentials. You agree to provide accurate, current, and complete information and
              to promptly update any changes. You are responsible for all activity that occurs under your account. We reserve the right to suspend
              or terminate accounts that violate these terms.
            </p>
            <p>
              You may request deletion of your account at any time by contacting us at{' '}
              <a href="mailto:contact@kikikata.app">contact@kikikata.app</a>. Upon deletion, your personal data will be removed in accordance with
              our <a href="/privacy-policy">Privacy Policy</a>.
            </p>

            <h2>4. Use of Our Services</h2>
            <p>
              You agree to use Kikikata solely for lawful purposes and in accordance with these terms. You may not use our services to distribute
              spam, engage in illegal activity, attempt to gain unauthorized access to our systems, or disrupt the service for other users. We reserve
              the right to suspend or terminate your account for any misuse, at our sole discretion.
            </p>

            <h2>5. Intellectual Property</h2>
            <p>
              Our original content on Kikikata &mdash; including the application code, design, and interface &mdash; is protected by intellectual property
              laws and owned by or licensed to Kikikata. You may not reproduce, distribute, modify, or create derivative works from our original
              content without our prior written permission.
            </p>
            <p>
              Some educational content (such as vocabulary and sentences) may be sourced from publicly available community resources. We do not
              claim ownership of such third-party content. See our Content Disclaimer (Section 10) and Content Removal Policy (Section 11) for
              how we handle third-party content and intellectual property claims.
            </p>

            <h2>6. Free Content Access</h2>
            <p>
              All words, sentences, and audio on Kikikata are publicly accessible to everyone &mdash; no account or payment is required to browse or
              listen to any content, regardless of JLPT level (N1&ndash;N5). We do not charge for access to content data.
            </p>

            <h2>7. Subscription Services and Payments</h2>
            <p>
              A paid subscription unlocks the Spaced Repetition System (SRS) learning and review functionality for JLPT N1&ndash;N4 level items. Free
              users can use the full SRS system for N5-level items at no cost. The subscription covers the SRS features only &mdash; not the underlying
              content, which remains freely accessible as described above.
            </p>
            <p>
              We offer the following plans, processed securely through PayPal. Current prices are listed on our{' '}
              <a href="/pricing">Pricing page</a> and may vary during promotional periods or sales:
            </p>
            <ul>
              <li><strong>Monthly Plan</strong>: Billed monthly. You may cancel at any time.</li>
              <li><strong>Yearly Plan</strong>: Billed annually. You may cancel at any time.</li>
              <li><strong>Lifetime Plan</strong>: A one-time payment granting permanent access to premium SRS features. Promotional pricing may apply during sales.</li>
            </ul>
            <p>
              By subscribing, you authorize PayPal to charge the applicable fees to your payment method on the billing cycle you selected. Prices
              are subject to change with reasonable notice.
            </p>
            <p>
              <strong>Cancellation &amp; Refunds:</strong> You may cancel a Monthly or Yearly subscription at any time through your account settings
              or by contacting us. Cancellation takes effect at the end of the current billing period &mdash; you retain access to premium SRS features
              until then. No partial refunds are issued for unused time in a billing period. The Lifetime plan is a one-time, non-refundable purchase.
            </p>

            <h2>8. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by applicable law, Kikikata is not liable for any indirect, incidental, special, consequential, or
              punitive damages resulting from your use of, or inability to use, our services. This includes, without limitation, damages for loss
              of data, loss of progress, loss of profit, or interruption of service. Our total liability for any claim arising under these terms
              shall not exceed the amount you paid us in the twelve months preceding the claim.
            </p>

            <h2>9. Disclaimer of Warranties</h2>
            <p>
              Our services are provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without warranties of any kind, either express or implied. We do not warrant
              that the service will be uninterrupted, error-free, or free of harmful components.
            </p>

            <h2>10. Content Disclaimer</h2>
            <p>
              Some content, such as sentences and vocabulary, may be sourced from publicly available resources, including community-shared
              language-learning materials. While we make every effort to credit original creators where required, we cannot guarantee the accuracy
              or completeness of such content. If you are the creator of any content used on our platform and would like it removed or credited,
              please contact us at <a href="mailto:contact@kikikata.app">contact@kikikata.app</a>.
            </p>

            <h2>11. Content Removal Policy</h2>
            <p>
              If you believe that any content on Kikikata infringes your copyright or other intellectual property rights, please contact us at{' '}
              <a href="mailto:contact@kikikata.app">contact@kikikata.app</a> with full details of your claim. We will respond promptly to
              investigate and, where appropriate, remove or address the infringing content.
            </p>

            <h2>12. Third-Party Services</h2>
            <p>
              Our services rely on third-party providers including Google Firebase (authentication and data storage), PayPal (payment processing),
              Crisp (live chat support), and Amazon Web Services (email delivery). Your use of Kikikata is also subject to the applicable terms
              and privacy policies of these providers. We are not responsible for the practices of these third parties.
            </p>

            <h2>13. Changes to These Terms</h2>
            <p>
              We may modify these Terms of Service at any time. Material changes will be communicated by updating the date at the top of this page.
              Your continued use of the service following any changes constitutes your acceptance of the revised terms. Please review this page
              periodically.
            </p>

            <h2>14. Governing Law</h2>
            <p>
              These terms are governed by and construed in accordance with applicable law. Any disputes arising under these terms shall be resolved
              through good-faith negotiation before pursuing any formal legal remedy.
            </p>

            <h2>15. Contact Us</h2>
            <p>
              If you have any questions or concerns about these Terms of Service, please contact us at{' '}
              <a href="mailto:contact@kikikata.app">contact@kikikata.app</a>.
            </p>
          </article>
        </div>
      </main>

      <MainFooter />
    </div>
  );
};

export default TermsOfServicePage;